import { Box, Chip, Grid, styled, Typography } from "~/common/mui";
import { Card } from "~/features/configurator/Linear/components/Card";
import type { AccountInfoData } from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import { C_ON_PRIMARY_CONTAINER, C_PRIMARY_CONTAINER } from "~/common/components/icon/Colors";
import Parameter from "~/common/components/Parameter";
import { useTranslation } from "react-i18next";
import { NoResultsFound } from "~/pages/AccountSelectionPage/AccountSelectionPage";
import { useIsMinDesktopSizeL } from "~/common/hooks/useMediaQuery";
import CheckIcon from "@mui/icons-material/Check";

interface NewAccountSelectionGridProps {
  accounts: AccountInfoData[];
  selectedAccount: AccountInfoData | undefined;
  onAccountClick: (account: AccountInfoData) => void;
  className?: string;
}

function isSchaefflerEmail(email?: string): boolean {
  if (!email) {
    return false;
  }

  const parts = email.toLowerCase().split("@");
  if (parts.length !== 2) {
    return false;
  }

  const domain = parts[1];
  return domain === "schaeffler.com";
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "$selected",
})<{ $selected?: boolean }>(({ theme, $selected }) => ({
  height: "100%",
  minHeight: "100%",
  padding: theme.spacing(2),
  "&::after": {
    borderRadius: "12px !important",
    ...($selected && {
      borderColor: `${theme.palette.primary.main} !important`,
    }),
  },
  [theme.breakpoints.up("desktop-m")]: {
    padding: theme.spacing(3),
  },
}));

const GridContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "$selected",
})<{ $selected?: boolean }>(({ theme, $selected }) => ({
  height: "100%",
  minHeight: "100%",
  display: "grid",
  gap: `${theme.spacing(1)} ${theme.spacing(3)}`,
  gridTemplateColumns: $selected ? "1fr 1fr 1fr auto" : "1fr 1fr 1fr",
  gridTemplateRows: $selected ? "auto auto auto" : "auto auto",
  gridTemplateAreas: $selected
    ? `"select select select select" "name name name name" "id salesOrg currency ."`
    : `"name name name" "id salesOrg currency"`,
  "& > div": {
    minWidth: 0,
    minHeight: 0,
    alignContent: "start",
  },
  [theme.breakpoints.up("desktop-m")]: {
    gap: `${theme.spacing(2)} ${theme.spacing(3)}`,
    ...($selected
      ? {
          gridTemplateRows: "auto auto",
          gridTemplateAreas: `"name name select select" "id salesOrg currency ."`,
        }
      : {
          gridTemplateAreas: `"name name name name" "id salesOrg currency ."`,
        }),
  },
}));

const AccountTile = ({
  account,
  selected,
  onClick,
}: {
  account: AccountInfoData;
  selected: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const email = window.SCHA?.user.email;

  return (
    <Grid
      key={account.id}
      size={{
        phone: 12,
        tablet: 6,
        "desktop-s": 4,
      }}
    >
      <StyledCard $selected={selected} onClick={onClick}>
        <GridContainer $selected={selected}>
          <Box sx={{ gridArea: "name", mb: { phone: 1, "desktop-m": 0 } }}>
            <Typography variant="bodyLarge">{account.name}</Typography>
          </Box>
          {selected && (
            <Box
              sx={{
                gridArea: "select",
                display: "flex",
                justifyContent: { phone: "start", "desktop-m": "end" },
                alignItems: { "desktop-m": "start" },
              }}
            >
              <Chip
                sx={{
                  borderRadius: "8px",
                  padding: "0 8px",
                  background: C_PRIMARY_CONTAINER,
                  color: C_ON_PRIMARY_CONTAINER,
                }}
                label={t("accountSelection.selected")}
                icon={<CheckIcon color="success"  />}
              />
            </Box>
          )}
          <Box sx={{ gridArea: "id", alignContent: "end" }}>
            <Parameter label={t("accountSelectionPage.account")}>{account.number}</Parameter>
          </Box>
          {isSchaefflerEmail(email) && (
            <Box sx={{ gridArea: "salesOrg", alignContent: "end" }}>
              <Parameter label={t("accountSelectionPage.salesOrg")}>{account.salesOrg}</Parameter>
            </Box>
          )}
          <Box sx={{ gridArea: "currency", alignContent: "end" }}>
            <Parameter label={t("page.accountSelection.tile.currency")}>{account.currency}</Parameter>
          </Box>
        </GridContainer>
      </StyledCard>
    </Grid>
  );
};

const NewAccountSelectionGrid = ({
  accounts,
  selectedAccount,
  onAccountClick,
  className,
}: NewAccountSelectionGridProps) => {
  const { t } = useTranslation();
  const isDesktopL = useIsMinDesktopSizeL();
  return (
    <Box>
      <Grid
        container
        spacing={isDesktopL ? 4 : 3}
        sx={{
          alignItems: "stretch",
          alignContent: "stretch",
          minHeight: "100%",
        }}
      >
        {accounts.map((account) => (
          <AccountTile
            key={account.id}
            account={account}
            selected={account === selectedAccount}
            onClick={() => onAccountClick(account)}
          />
        ))}
        {accounts.length === 0 && <NoResultsFound />}
      </Grid>
    </Box>
  );
};

export default NewAccountSelectionGrid;
